import { useStore } from 'vuex'
import { numberFormatAddComma } from '@/utils/commonMethods'

interface Condition {
  name: string
  value: string
}
interface Master {
  id: string
  opt_id?: string
  option_id?: string
  option_name: string
}

export const useSearchCondition = () => {
  const store = useStore()

  const getSearchConditionFromQuery = (
    query: any,
    type: 'hospital' | 'latter' = 'hospital'
  ) => {
    const conditions: Condition[] = []
    // 共通：病院所在地
    if (query.prefectures?.length > 0) {
      conditions.push({
        name: '病院所在地',
        value: store.state.master.prefectures
          .filter((el: Master) => query.prefectures.includes(el.opt_id))
          .map((el: Master) => el.option_name)
          .join('／')
      })
    }
    // 専門研修：専門研修プログラム科目
    if (query.program_subjects?.length > 0) {
      conditions.push({
        name: '専門研修プログラム科目',
        value: store.state.master.programSubjects
          .filter((el: Master) => query.program_subjects.includes(el.opt_id))
          .map((el: Master) => el.option_name)
          .join('／')
      })
    }
    // 共通：病院種別
    if (query.hospital_class?.length > 0) {
      conditions.push({
        name: '病院種別',
        value: store.state.master.hospitalClass
          .filter((el: Master) => query.hospital_class.includes(el.opt_id))
          .map((el: Master) => el.option_name)
          .join('／')
      })
    }
    // 専門研修：プログラムの特徴
    if (query.feature_tag?.length > 0) {
      conditions.push({
        name: 'プログラムの特徴',
        value: store.state.master.featureTag
          .filter((el: Master) => query.feature_tag.includes(el.opt_id))
          .map((el: Master) => el.option_name)
          .join('／')
      })
    }
    // 共通：救急指定
    if (query.designated_hospital?.length > 0) {
      conditions.push({
        name: '救急指定',
        value: store.state.master.designatedHospital
          .filter((el: Master) => query.designated_hospital.includes(el.opt_id))
          .map((el: Master) => el.option_name)
          .join('／')
      })
    }
    // 病院：こだわり条件 / 専門研修：病院の特徴
    if (query.discerning_conditions?.length > 0) {
      conditions.push({
        name: type === 'hospital' ? 'こだわり条件' : '病院の特徴',
        value: store.state.master.keywords
          .filter((el: Master) =>
            query.discerning_conditions.includes(el.opt_id)
          )
          .map((el: Master) => el.option_name)
          .join('／')
      })
    }
    // 病院：その他希望の条件
    if (
      query.bonus ||
      query.overtime_hours_mavg ||
      query.house ||
      query.j_graduating_school
    ) {
      const values = []

      if (query.bonus) values.push('賞与あり')
      if (query.overtime_hours_mavg) values.push('時間外勤務あり')
      if (query.house) values.push('研修医のための宿舎あり')
      if (query.j_graduating_school) values.push('出身大学の先輩あり')

      conditions.push({
        name: 'その他希望の条件',
        value: values.join('／')
      })
    }
    // 共通: 病床数
    if (query.bed_num_lo || query.bed_num_hi) {
      let value = query.bed_num_lo
        ? numberFormatAddComma(Number(query.bed_num_lo)) + '床'
        : ''
      value += ' 〜 '
      value += query.bed_num_hi
        ? numberFormatAddComma(Number(query.bed_num_hi)) + '床'
        : ''

      conditions.push({
        name: '病床数',
        value: value
      })
    }
    // 専門研修: 当科病床数
    if (query.department_bed_num_lo || query.department_bed_num_hi) {
      let value = query.department_bed_num_lo
        ? numberFormatAddComma(Number(query.department_bed_num_lo)) + '床'
        : ''
      value += ' 〜 '
      value += query.department_bed_num_hi
        ? numberFormatAddComma(Number(query.department_bed_num_hi)) + '床'
        : ''

      conditions.push({
        name: '当科病床数',
        value: value
      })
    }
    // 専門研修: 当科在籍研修医数
    if (query.resident_num_lo || query.resident_num_hi) {
      let value = query.resident_num_lo
        ? numberFormatAddComma(Number(query.resident_num_lo)) + '人'
        : ''
      value += ' 〜 '
      value += query.resident_num_hi
        ? numberFormatAddComma(Number(query.resident_num_hi)) + '人'
        : ''

      conditions.push({
        name: '当科在籍研修医数',
        value: value
      })
    }
    // 専門研修: 当科在籍指導医数
    if (query.instructor_num_lo || query.instructor_num_hi) {
      let value = query.instructor_num_lo
        ? numberFormatAddComma(Number(query.instructor_num_lo)) + '人'
        : ''
      value += ' 〜 '
      value += query.instructor_num_hi
        ? numberFormatAddComma(Number(query.instructor_num_hi)) + '人'
        : ''

      conditions.push({
        name: '当科在籍指導医数',
        value: value
      })
    }
    // 共通: 医師数
    if (query.doctor_num_lo || query.doctor_num_hi) {
      let value = query.doctor_num_lo
        ? numberFormatAddComma(Number(query.doctor_num_lo)) + '人'
        : ''
      value += ' 〜 '
      value += query.doctor_num_hi
        ? numberFormatAddComma(Number(query.doctor_num_hi)) + '人'
        : ''

      conditions.push({
        name: '医師数',
        value: value
      })
    }
    // 病院：募集定員
    if (query.positions_num_lo || query.positions_num_hi) {
      let value = query.positions_num_lo
        ? numberFormatAddComma(Number(query.positions_num_lo)) + '人'
        : ''
      value += ' 〜 '
      value += query.positions_num_hi
        ? numberFormatAddComma(Number(query.positions_num_hi)) + '人'
        : ''

      conditions.push({
        name: '募集定員',
        value: value
      })
    }
    // 専門研修：募集定員（採用予定人数）
    if (query.recruitment_num_lo || query.recruitment_num_hi) {
      let value = query.recruitment_num_lo
        ? numberFormatAddComma(Number(query.recruitment_num_lo)) + '人'
        : ''
      value += ' 〜 '
      value += query.recruitment_num_hi
        ? numberFormatAddComma(Number(query.recruitment_num_hi)) + '人'
        : ''

      conditions.push({
        name: '募集定員（採用予定人数）',
        value: value
      })
    }
    // 病院：年収
    if (query.income_lo || query.income_hi) {
      let value = query.income_lo
        ? numberFormatAddComma(Number(query.income_lo)) + '万円'
        : ''
      value += ' 〜 '
      value += query.income_hi
        ? numberFormatAddComma(Number(query.income_hi)) + '万円'
        : ''

      conditions.push({
        name: '年収',
        value: value
      })
    }
    // 病院：当直回数
    if (query.shifts_num_lo || query.shifts_num_hi) {
      let value = query.shifts_num_lo
        ? numberFormatAddComma(Number(query.shifts_num_lo)) + '回'
        : ''
      value += ' 〜 '
      value += query.shifts_num_hi
        ? numberFormatAddComma(Number(query.shifts_num_hi)) + '回'
        : ''

      conditions.push({
        name: '当直回数',
        value: value
      })
    }
    // 専門研修：年収
    if (query.s_salary3_num_lo || query.s_salary3_num_hi) {
      let value = query.s_salary3_num_lo
        ? numberFormatAddComma(Number(query.s_salary3_num_lo)) + '万円'
        : ''
      value += ' 〜 '
      value += query.s_salary3_num_hi
        ? numberFormatAddComma(Number(query.s_salary3_num_hi)) + '万円'
        : ''

      conditions.push({
        name: '年収',
        value: value
      })
    }
    // 専門研修：当直回数
    if (query.s_shifts3_num_lo || query.s_shifts3_num_hi) {
      let value = query.s_shifts3_num_lo
        ? numberFormatAddComma(Number(query.s_shifts3_num_lo)) + '回'
        : ''
      value += ' 〜 '
      value += query.s_shifts3_num_hi
        ? numberFormatAddComma(Number(query.s_shifts3_num_hi)) + '回'
        : ''

      conditions.push({
        name: '当直回数',
        value: value
      })
    }
    // 共通：フリーワード
    if (query.free_word) {
      conditions.push({
        name: 'フリーワード',
        value: decodeURIComponent(query.free_word)
      })
    }

    return conditions
  }

  const getSearchConditionTag = (currentCondition: Condition[]) => {
    const tags: string[] = []

    currentCondition.forEach((el: Condition) => {
      if (el.value.indexOf('／') > -1) {
        el.value.split('／').forEach((val: string) => tags.push(val))
        return
      }

      if (el.value.indexOf('〜') > -1) {
        tags.push(`${el.name}：${el.value}`)
        return
      }

      tags.push(el.value)
    })

    return tags
  }

  return {
    getSearchConditionFromQuery,
    getSearchConditionTag
  }
}
